import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Icon } from '../icon'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { VStack } from '../vstack'

export interface SelectListItemProps {
    label: React.ReactNode
    icon?: React.ElementType
    dotColor?: string
    selected?: boolean
    isDisabled?: boolean
    onPress?: () => void
    testID?: string
}

export const SelectListItem: React.FC<SelectListItemProps> = ({
    label,
    icon,
    dotColor,
    selected,
    isDisabled,
    onPress,
    testID,
}) => (
    <Pressable
        onPress={isDisabled ? undefined : onPress}
        disabled={isDisabled}
        accessibilityRole='button'
        testID={testID}
        className={`h-12 flex-row items-center gap-2 px-6 py-3 ${
            isDisabled
                ? 'opacity-40'
                : selected
                  ? 'bg-background-surface-hover'
                  : 'hover:bg-background-surface-hover'
        }`}
    >
        {icon ? (
            <Icon
                as={icon}
                size={24}
                className='text-content-default'
                {...(testID ? { testID: `${testID}-icon` } : {})}
            />
        ) : null}
        {dotColor ? (
            <Box className='h-6 w-6 items-center justify-center'>
                <Box className='h-3 w-3 rounded-full' style={{ backgroundColor: dotColor }} />
            </Box>
        ) : null}
        {typeof label === 'string' || typeof label === 'number' ? (
            <Text
                weight='medium'
                numberOfLines={1}
                className='text-content-default flex-1 text-[16px] leading-[24px]'
            >
                {label}
            </Text>
        ) : (
            // A rendered label is a View, and nested in the Text above RN lays it
            // out as an inline attachment against that 24px line box, so the row
            // sliced it mid-glyph (AOS-1319). Same discrimination as PickerField.
            <Box className='min-w-0 flex-1 flex-row items-center'>{label}</Box>
        )}
    </Pressable>
)

export interface SelectListProps {
    title: React.ReactNode
    description?: React.ReactNode
    footer?: React.ReactNode
    showHandle?: boolean
    /** Rule between the title block and the content (Figma `upper-line`). */
    showUpperLine?: boolean
    /** Rule between the content and the footer slot (Figma `showDownLine`). */
    showDownLine?: boolean
    children: React.ReactNode
    className?: string
    /** Overrides the footer band's own padding. */
    footerClassName?: string
}

export const SelectList: React.FC<SelectListProps> = ({
    title,
    description,
    footer,
    showHandle = true,
    showUpperLine = true,
    showDownLine = true,
    children,
    className,
    footerClassName,
}) => (
    <Box
        className={`w-full overflow-hidden rounded-t-2xl bg-background-surface ${className ?? ''}`}
    >
        {showHandle ? (
            <Box className='items-center pt-3'>
                <Box className='h-[2px] w-12 rounded-full bg-graphics-grey' />
            </Box>
        ) : null}
        <VStack className='gap-3 px-2 pt-6'>
            <VStack className='gap-2 pl-4'>
                <Text weight='medium' className='text-content-default text-[18px] leading-[24px]'>
                    {title}
                </Text>
                {description ? (
                    <Text className='text-content-muted text-[16px] leading-[24px]'>
                        {description}
                    </Text>
                ) : null}
            </VStack>
            {showUpperLine ? <Box className='border-b border-border-subtle border-solid' /> : null}
        </VStack>
        <VStack className='shrink py-4'>{children}</VStack>
        {footer ? (
            <>
                {showDownLine ? (
                    <Box className='border-b border-border-subtle border-solid' />
                ) : null}
                <Box className={twMerge('px-2 pb-4 pt-4', footerClassName)}>{footer}</Box>
            </>
        ) : null}
    </Box>
)
